/**
 * Utility functions for mapping paths to screen IDs and names
 * Used for context-aware mode indicators
 */

export const getScreenIdFromPath = (path: string): string | undefined => {
  // Remove leading/trailing slashes and query parameters
  const cleanPath = path.split('?')[0].replace(/^\/|\/$/g, '');
  
  const pathMap: Record<string, string> = {
    '': 'dashboard',
    'dashboard': 'dashboard',
    'timesheet': 'timesheet',
    'my-timesheets': 'timesheet',
    'approvals': 'approvals',
    'reports/timesheet': 'reports-timesheet',
    'reports/utilization': 'reports-utilization',
    'reports/billable': 'reports-billable',
    'reports/project': 'reports-project',
    'reports/custom': 'reports-custom',
    'analytics': 'analytics',
    'team/members': 'team-members',
    'team/schedule': 'team-schedule',
    'team/capacity': 'team-capacity',
    'projects/active': 'projects-active',
    'projects/archive': 'projects-archive',
    'projects/budget': 'projects-budget',
    'integrations': 'integrations',
    'integrations/erp': 'integrations', // Legacy redirect
    'integrations/hr': 'integrations', // Legacy redirect
    'integrations/sync': 'integrations', // Legacy redirect
    'import': 'import',
    'export': 'export',
    // Settings pages
    'settings/general': 'settings-general',
    'settings/roles': 'roles-rates',
    'settings/approval': 'settings-approval',
  };
  
  return pathMap[cleanPath];
};

export const getScreenNameFromId = (screenId: string): string => {
  const nameMap: Record<string, string> = {
    'dashboard': 'Dashboard',
    'timesheet': 'My Timesheet',
    'approvals': 'Approvals',
    'reports-timesheet': 'Timesheet Reports',
    'reports-utilization': 'Utilization Reports',
    'reports-billable': 'Billable Hours',
    'reports-project': 'Project Reports',
    'reports-custom': 'Custom Reports',
    'analytics': 'Analytics',
    'team-members': 'Team Members',
    'team-schedule': 'Team Schedule',
    'team-capacity': 'Capacity Planning',
    'projects-active': 'Active Projects',
    'projects-archive': 'Archived Projects',
    'projects-budget': 'Budget Tracking',
    'integrations': 'Integrations',
    'import': 'Import Data',
    'export': 'Export Data',
    // Settings pages
    'settings-general': 'General Settings',
    'roles-rates': 'Roles & Rates',
    'settings-approval': 'Approval Rules',
  };
  
  return nameMap[screenId] || 'Unknown Screen';
};

/**
 * Check if a path represents a settings page
 */
export const isSettingsPath = (path: string): boolean => {
  return path.startsWith('/settings');
};

/**
 * Get a user-friendly name for any path
 */
export const getPathDisplayName = (path: string): string => {
  const screenId = getScreenIdFromPath(path);
  
  if (screenId) {
    return getScreenNameFromId(screenId);
  }
  
  if (isSettingsPath(path)) {
    return 'Settings';
  }
  
  // Fallback: capitalize and format path
  const cleanPath = path.replace(/^\/|\/$/g, '');
  return cleanPath
    .split('/')
    .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' > ');
};
